import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { HiPlus, HiPencil, HiTrash, HiX, HiEye, HiEyeOff, HiSearch } from "react-icons/hi";
import { MdOutlineFireplace } from "react-icons/md";
import {
  useGetFirebaseConnectionsMutation,
  useAddFirebaseConnectionMutation,
  useUpdateFirebaseConnectionMutation,
  useDeleteFirebaseConnectionMutation,
} from "../../../features/admin/firebaseSetup/firebaseSetupApi";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";
import { Drawer, Button } from "../../../components/ui";

const PAGE_SIZE = 10;

// ── Credential view modal ─────────────────────────────────────────────────────
const CredentialModal = ({ row, onClose }) => {
  const [showKey, setShowKey] = useState(false);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "14px", width: "480px", maxWidth: "92vw", boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <p style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>Firebase Credentials</p>
            <p style={{ margin: 0, fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
              {row.company_name && <><strong>{row.company_name}</strong> · </>}Tenant: <strong>{row.tenant_id}</strong>
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: "4px", display: "flex" }}>
            <HiX size={18} />
          </button>
        </div>
        <div style={{ padding: "20px" }}>
          {[
            { label: "Project ID",    value: row.project_id,    sensitive: false },
            { label: "Client Email",  value: row.client_email,  sensitive: false },
            { label: "Private Key",   value: row.private_key,   sensitive: true  },
          ].map(({ label, value, sensitive }) => (
            <div key={label} style={{ marginBottom: "14px" }}>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>{label}</label>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input readOnly type={sensitive && !showKey ? "password" : "text"} value={value || "—"}
                  style={{ flex: 1, padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "12px", background: "#f8fafc", color: "#0f172a", outline: "none", fontFamily: "monospace" }} />
                {sensitive && (
                  <button type="button" onClick={() => setShowKey(p => !p)}
                    style={{ padding: "8px", border: "1px solid #e2e8f0", borderRadius: "6px", background: "#fff", cursor: "pointer", color: "#64748b", display: "flex", flexShrink: 0 }}>
                    {showKey ? <HiEyeOff size={15} /> : <HiEye size={15} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const emptyForm = { tenant_id: "", project_id: "", client_email: "", private_key: "" };

const AllFirebaseSetup = () => {
  const token = () => sessionStorage.getItem("access_token");

  const [limit] = useState(PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [viewRow, setViewRow] = useState(null);
  const [drawer, setDrawer] = useState(null); // null | { mode: 'add' } | { mode: 'edit', row }
  const [form, setForm] = useState(emptyForm);
  const debounceRef = useRef(null);

  const { data: vendorsData } = useFetchVendorsQuery(token());
  const vendors = vendorsData?.body || [];

  const [getConnections, { data, isLoading }] = useGetFirebaseConnectionsMutation();
  const [addConnection, { isLoading: adding }] = useAddFirebaseConnectionMutation();
  const [updateConnection, { isLoading: updating }] = useUpdateFirebaseConnectionMutation();
  const [deleteConnection] = useDeleteFirebaseConnectionMutation();

  const fetch = (off = offset, q = search) => {
    getConnections({ token: token(), limit, offset: off, search: q });
  };

  useEffect(() => { fetch(); }, [offset]);

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearch(q);
    setCurrentPage(1);
    setOffset(0);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetch(0, q), 350);
  };

  const rows = data?.body?.data || [];
  const total = data?.body?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // tenant_ids that already have a connection — used to grey out dropdown options
  const configuredTenantIds = new Set(rows.map(r => r.tenant_id));

  const openAdd = () => {
    setForm(emptyForm);
    setDrawer({ mode: "add" });
  };

  const openEdit = (row) => {
    setForm({ tenant_id: row.tenant_id || "", project_id: row.project_id || "", client_email: row.client_email || "", private_key: row.private_key || "" });
    setDrawer({ mode: "edit", row });
  };

  const closeDrawer = () => setDrawer(null);

  const handleSave = async () => {
    if (drawer.mode === "add") {
      if (!form.tenant_id)   { toast.error("Select a tenant");    return; }
      if (configuredTenantIds.has(form.tenant_id)) { toast.error("This tenant already has a Firebase connection"); return; }
    }
    if (!form.project_id)   { toast.error("Enter Project ID");   return; }
    if (!form.client_email) { toast.error("Enter Client Email"); return; }
    if (!form.private_key)  { toast.error("Enter Private Key");  return; }

    try {
      if (drawer.mode === "edit") {
        const body = {};
        if (form.project_id   !== drawer.row.project_id)   body.project_id   = form.project_id;
        if (form.client_email !== drawer.row.client_email) body.client_email = form.client_email;
        if (form.private_key  !== drawer.row.private_key)  body.private_key  = form.private_key;
        if (!Object.keys(body).length) { toast("No changes made"); closeDrawer(); return; }
        await updateConnection({ token: token(), id: drawer.row.id, body }).unwrap();
        toast.success("Firebase connection updated");
      } else {
        await addConnection({ token: token(), body: form }).unwrap();
        toast.success("Firebase connection added");
      }
      closeDrawer();
      fetch();
    } catch (err) {
      if (err?.status === 409) toast.error("This tenant already has a Firebase connection");
      else toast.error(err?.data?.message || "Something went wrong");
    }
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Delete Firebase connection for "${row.company_name || row.tenant_id}"?`)) return;
    deleteConnection({ token: token(), id: row.id })
      .unwrap()
      .then(() => { toast.success("Deleted"); fetch(); })
      .catch(() => toast.error("Delete failed"));
  };

  const busy = adding || updating;
  const inp = { border: "1px solid #e2e8f0", borderRadius: "8px", padding: "9px 12px", width: "100%", fontSize: "14px", outline: "none", background: "#fff", boxSizing: "border-box" };
  const lbl = { display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "6px" };
  const field = { marginBottom: "18px" };

  const COLS = ["#", "Company", "Tenant ID", "Project ID", "Credentials", "Created", ""];

  return (
    <div className="page-content">
      {viewRow && <CredentialModal row={viewRow} onClose={() => setViewRow(null)} />}

      <div style={{ padding: "24px" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <MdOutlineFireplace size={22} style={{ color: "#f97316" }} />
            <div>
              <h4 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#0f172a" }}>Firebase Setup</h4>
              <p style={{ margin: 0, fontSize: "12px", color: "#64748b", marginTop: "2px" }}>Per-tenant Firebase project connections</p>
            </div>
          </div>
          <button onClick={openAdd}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "9px 16px", background: "#f97316", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}>
            <HiPlus size={16} /> Add Connection
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: "16px", position: "relative", maxWidth: "360px" }}>
          <HiSearch size={15} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Search by company, tenant ID or project ID…"
            style={{ ...inp, paddingLeft: "34px", fontSize: "13px" }}
          />
        </div>

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                {COLS.map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600, color: "#64748b", whiteSpace: "nowrap", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}>
                    <MdOutlineFireplace size={32} style={{ marginBottom: "8px", opacity: 0.3 }} />
                    <div>{search ? `No results for "${search}"` : "No Firebase connections found."}</div>
                  </td>
                </tr>
              ) : rows.map((r, idx) => (
                <tr key={r.id} style={{ borderBottom: "1px solid #f1f5f9" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td style={{ padding: "12px 16px", color: "#94a3b8", fontSize: "12px" }}>{offset + idx + 1}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ fontWeight: 600, color: "#0f172a", fontSize: "13px" }}>{r.company_name || "—"}</span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ padding: "3px 10px", background: "#fff7ed", color: "#c2410c", borderRadius: "20px", fontSize: "11px", fontWeight: 600, fontFamily: "monospace" }}>
                      {r.tenant_id}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px", fontFamily: "monospace", color: "#374151", fontSize: "12px" }}>{r.project_id || "—"}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <button onClick={() => setViewRow(r)}
                      style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "5px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "12px", color: "#374151", cursor: "pointer" }}>
                      <HiEye size={13} /> View
                    </button>
                  </td>
                  <td style={{ padding: "12px 16px", color: "#64748b", whiteSpace: "nowrap", fontSize: "12px" }}>
                    {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button onClick={() => openEdit(r)}
                        style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "5px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "12px", color: "#374151", cursor: "pointer" }}>
                        <HiPencil size={12} /> Edit
                      </button>
                      <button onClick={() => handleDelete(r)}
                        style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "5px 10px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: "6px", fontSize: "12px", color: "#e11d48", cursor: "pointer" }}>
                        <HiTrash size={12} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
              <span style={{ fontSize: "12px", color: "#64748b" }}>Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
              <div style={{ display: "flex", gap: "6px" }}>
                <button disabled={currentPage === 1}
                  onClick={() => { const p = currentPage - 1; setCurrentPage(p); setOffset((p - 1) * limit); }}
                  style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: "6px", background: currentPage === 1 ? "#f8fafc" : "#fff", cursor: currentPage === 1 ? "not-allowed" : "pointer", fontSize: "12px", color: "#374151" }}>
                  Previous
                </button>
                <button disabled={currentPage === totalPages}
                  onClick={() => { const p = currentPage + 1; setCurrentPage(p); setOffset((p - 1) * limit); }}
                  style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: "6px", background: currentPage === totalPages ? "#f8fafc" : "#fff", cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontSize: "12px", color: "#374151" }}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Drawer */}
      {drawer && (
        <Drawer
          width="md"
          title={drawer.mode === "add" ? "Add Firebase Connection" : `Edit — ${drawer.row.company_name || drawer.row.tenant_id}`}
          onClose={closeDrawer}
          footer={
            <>
              <Button variant="outline" onClick={closeDrawer}>Cancel</Button>
              <Button variant="primary" onClick={handleSave} disabled={busy}>
                {busy ? "Saving…" : drawer.mode === "add" ? "Add Connection" : "Update Connection"}
              </Button>
            </>
          }
        >
          <div style={{ padding: "24px" }}>

            {drawer.mode === "add" ? (
              <div style={field}>
                <label style={lbl}>Tenant *</label>
                <select style={inp} value={form.tenant_id} onChange={e => setForm(p => ({ ...p, tenant_id: e.target.value }))}>
                  <option value="">— Select tenant —</option>
                  {vendors.map(v => {
                    const alreadyConfigured = configuredTenantIds.has(String(v.tenant_id));
                    return (
                      <option key={v.company_id} value={String(v.tenant_id)} disabled={alreadyConfigured}>
                        {v.name} ({v.tenant_id}){alreadyConfigured ? " — already configured" : ""}
                      </option>
                    );
                  })}
                </select>
                {form.tenant_id && configuredTenantIds.has(form.tenant_id) && (
                  <p style={{ margin: "6px 0 0", fontSize: "12px", color: "#dc2626" }}>
                    This tenant already has a Firebase connection.
                  </p>
                )}
              </div>
            ) : (
              <div style={field}>
                <label style={lbl}>Tenant</label>
                <input style={{ ...inp, background: "#f8fafc", color: "#64748b" }} readOnly value={`${drawer.row.company_name ? drawer.row.company_name + " · " : ""}${form.tenant_id}`} />
              </div>
            )}

            <div style={field}>
              <label style={lbl}>Project ID *</label>
              <input style={inp} type="text" value={form.project_id}
                placeholder="e.g. my-firebase-project"
                onChange={e => setForm(p => ({ ...p, project_id: e.target.value }))} />
            </div>

            <div style={field}>
              <label style={lbl}>Client Email *</label>
              <input style={inp} type="email" value={form.client_email}
                placeholder="firebase-adminsdk-xxx@project.iam.gserviceaccount.com"
                onChange={e => setForm(p => ({ ...p, client_email: e.target.value }))} />
            </div>

            <div style={field}>
              <label style={lbl}>Private Key *</label>
              <textarea
                style={{ ...inp, height: "160px", resize: "vertical", fontFamily: "monospace", fontSize: "12px" }}
                value={form.private_key}
                placeholder={"-----BEGIN RSA PRIVATE KEY-----\n..."}
                onChange={e => setForm(p => ({ ...p, private_key: e.target.value }))} />
              <p style={{ margin: "4px 0 0", fontSize: "11px", color: "#94a3b8" }}>
                Paste the full private_key value from your Firebase service account JSON.
              </p>
            </div>

          </div>
        </Drawer>
      )}
    </div>
  );
};

export default AllFirebaseSetup;
